"use client";

import { Label } from "@ui/components/label";
import { Textarea } from "@ui/components/textarea";
import { cn } from "@ui/lib";
import { CheckIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";

interface VirtualStagingInputProps {
	value: string;
	onChange: (value: string) => void;
	disabled?: boolean;
	className?: string;
}

export function VirtualStagingInput({
	value,
	onChange,
	disabled = false,
	className,
}: VirtualStagingInputProps) {
	const t = useTranslations("aiImageGenerator.client.virtualStaging");

	// Parse the value into style and details
	const parseValue = (fullPrompt: string) => {
		// Strip the suffix if present
		let cleaned = fullPrompt
			.replace(
				/\.?\s*Keep the existing room structure, door and window positions, and all soft furnishings from the original image unchanged\.?\s*$/i,
				"",
			)
			.trim();
		// Remove trailing period left after stripping
		cleaned = cleaned.replace(/\.\s*$/, "").trim();

		// Expected format: "StyleName interior style, details..."
		const match = cleaned.match(/^(.+?)\s+interior\s+style,\s*(.*)$/i);
		if (match) {
			return {
				style: match[1].trim(),
				details: match[2].trim(),
			};
		}
		return { style: "", details: cleaned };
	};

	const [style, setStyle] = useState(() => parseValue(value).style);
	const [details, setDetails] = useState(() => parseValue(value).details);

	// Sync with external value changes
	useEffect(() => {
		const parsed = parseValue(value);
		setStyle(parsed.style);
		setDetails(parsed.details);
	}, [value]);

	const PROMPT_SUFFIX =
		"Keep the existing room structure, door and window positions, and all soft furnishings from the original image unchanged.";

	// Combine and emit changes
	const buildPrompt = (s: string, d: string) => {
		if (!s && !d) return "";
		const raw = s ? `${s} interior style, ${d}` : d;
		const base = raw.replace(/\.+\s*$/, "");
		const result = `${base}. ${PROMPT_SUFFIX}`;
		console.log("[VirtualStaging] prompt:", result);
		return result;
	};

	const handleStyleChange = (newStyle: string) => {
		setStyle(newStyle);
		onChange(buildPrompt(newStyle, details));
	};

	const handleDetailsChange = (newDetails: string) => {
		setDetails(newDetails);
		onChange(buildPrompt(style, newDetails));
	};

	const styleCharCount = style.length;
	const detailsCharCount = details.length;
	const styleMaxLength = 20;
	const detailsMaxLength = 2000;

	return (
		<div className={cn("space-y-4", className)}>
			{/* Style Input */}
			<div className="space-y-2">
				<div className="flex items-center justify-between">
					<Label
						htmlFor="virtual-staging-style"
						className="text-sm font-medium"
					>
						{t("style.label")}
					</Label>
					<span
						className={cn(
							"text-xs",
							styleCharCount > styleMaxLength
								? "text-red-500"
								: "text-muted-foreground",
						)}
					>
						{styleCharCount}/{styleMaxLength}
					</span>
				</div>
				<div className="relative">
					<input
						id="virtual-staging-style"
						type="text"
						value={style}
						onChange={(e) => handleStyleChange(e.target.value)}
						disabled={disabled}
						maxLength={styleMaxLength}
						placeholder={t("style.placeholder")}
						className={cn(
							"flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm",
							"ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium",
							"placeholder:text-muted-foreground",
							"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
							"disabled:cursor-not-allowed disabled:opacity-50",
							styleCharCount === styleMaxLength && "pr-8",
						)}
					/>
					{styleCharCount === styleMaxLength && (
						<div className="absolute right-3 top-1/2 -translate-y-1/2">
							<CheckIcon className="h-4 w-4 text-primary" />
						</div>
					)}
				</div>
			</div>

			{/* Other Ideas Input */}
			<div className="space-y-2">
				<div className="flex items-center justify-between">
					<Label
						htmlFor="virtual-staging-details"
						className="text-sm font-medium"
					>
						{t("details.label")}
					</Label>
					<span
						className={cn(
							"text-xs",
							detailsCharCount > detailsMaxLength
								? "text-red-500"
								: "text-muted-foreground",
						)}
					>
						{detailsCharCount}/{detailsMaxLength}
					</span>
				</div>
				<div className="relative">
					<Textarea
						id="virtual-staging-details"
						value={details}
						onChange={(e) => handleDetailsChange(e.target.value)}
						disabled={disabled}
						maxLength={detailsMaxLength}
						placeholder={t("details.placeholder")}
						className={cn(
							"min-h-[120px] resize-none",
							detailsCharCount === detailsMaxLength && "pr-8",
						)}
					/>
					{detailsCharCount === detailsMaxLength && (
						<div className="absolute right-3 bottom-3">
							<CheckIcon className="h-4 w-4 text-primary" />
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
